import { useState, useEffect, useCallback } from 'react';
import { FileText, Printer } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';

type ClassRow = { id: number; name: string };
type Section = { id: number; section_label: string };
type Student = { id: number; display_name: string; roll_number: string | null };
type Term = { id: number; name: string };
type SubjectRow = { name: string; max_marks: number | null; is_absent: boolean; marks_obtained: number | null; percent: number | null; grade_label: string | null; grade_point: string | number | null };
type SkillRow = { name: string; is_absent: boolean; grade_label: string | null; grade_point: string | number | null };
type ExamView = { id: number; name: string; exam_type: string; subjects: SubjectRow[]; skills: SkillRow[]; summary: { marks_obtained: number | null; marks_max: number | null; percent: number | null; grade_label: string | null } };
type TermView = { id: number; name: string; weightage: string | number | null; exams: ExamView[]; summary: { percent: number | null } };
type Card = { student: { display_name: string }; terms: TermView[] };

function flattenClasses(data: any): ClassRow[] {
  return (data?.grouped || []).flatMap((g: any) => (g.classes || []).map((c: any) => ({ id: c.id, name: c.class_label })));
}

export default function ReportCardsPage() {
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [classId, setClassId] = useState<number | ''>('');
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionId, setSectionId] = useState<number | ''>('');
  const [students, setStudents] = useState<Student[]>([]);
  const [studentId, setStudentId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');

  useEffect(() => {
    apiRequest<any>(`${BASE}/classes`).then(r => setClasses(flattenClasses(r.data))).catch(() => {});
    apiRequest<any>(`${BASE}/exam-terms`).then(r => setTerms(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setSectionId(''); setSections([]); setStudents([]); setStudentId('');
    if (!classId) return;
    apiRequest<any>(`${BASE}/classes/${classId}/sections`).then(r => setSections(r.data?.data || r.data || [])).catch(() => {});
  }, [classId]);

  useEffect(() => {
    setStudents([]); setStudentId('');
    if (!sectionId) return;
    apiRequest<any>(`${BASE}/students?section_id=${sectionId}&limit=200`).then(r => setStudents(r.data?.data || [])).catch(() => {});
  }, [sectionId]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div className="page-head">
        <div className="page-head-icon"><FileText size={24} style={{ color: '#fff' }} /></div>
        <div className="page-head-text">
          <h1>Report Cards</h1>
          <p>View a student's live report card across exams and terms</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
        <Picker label="Class">
          <select className="select" value={classId} onChange={e => setClassId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Select class…</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Picker>
        <Picker label="Section">
          <select className="select" value={sectionId} onChange={e => setSectionId(e.target.value ? Number(e.target.value) : '')} disabled={!sections.length}>
            <option value="">{sections.length ? 'Select section…' : 'Pick a class'}</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.section_label}</option>)}
          </select>
        </Picker>
        <Picker label="Student">
          <select className="select" value={studentId} onChange={e => setStudentId(e.target.value ? Number(e.target.value) : '')} disabled={!students.length} style={{ minWidth: 200 }}>
            <option value="">{students.length ? 'Select student…' : 'Pick a section'}</option>
            {students.map(s => <option key={s.id} value={s.id}>{s.roll_number ? `${s.roll_number}. ` : ''}{s.display_name}</option>)}
          </select>
        </Picker>
        <Picker label="Term">
          <select className="select" value={termId} onChange={e => setTermId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">All terms</option>
            {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Picker>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {studentId
          ? <CardView studentId={studentId as number} termId={termId || null} />
          : <div className="empty"><div className="empty-art" style={{ background: 'var(--slate-soft)', color: 'var(--slate)' }}><FileText size={34} /></div><p>Pick a student to view their report card</p></div>}
      </div>
    </div>
  );
}

function CardView({ studentId, termId }: { studentId: number; termId: number | null }) {
  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const q = `student_id=${studentId}${termId ? `&term_id=${termId}` : ''}`;
    apiRequest<any>(`${BASE}/report-card?${q}`)
      .then(r => setCard(r.data))
      .catch(() => toastMessage('Failed to load report card', 'error'))
      .finally(() => setLoading(false));
  }, [studentId, termId]);
  useEffect(load, [load]);

  if (loading) return <p style={{ color: 'var(--text-2)' }}>Loading…</p>;
  if (!card) return null;

  const hasAny = card.terms.some(t => t.exams.length);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.4 }}>{card.student.display_name}</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Report card {termId ? '' : '· all terms'}</div>
        </div>
        <button className="btn btn-ghost" onClick={() => window.print()} style={{ marginLeft: 'auto', gap: 6 }}><Printer size={16} /> Print</button>
      </div>

      {!hasAny && <p style={{ color: 'var(--text-2)' }}>No published results yet for this student.</p>}

      {card.terms.map(term => term.exams.length > 0 && (
        <div key={term.id} style={{ marginBottom: 26 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{term.name}</h2>
            {term.summary.percent != null && <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Term average <b style={{ color: 'var(--text)' }}>{term.summary.percent}%</b></span>}
          </div>

          {term.exams.map(exam => (
            <div key={exam.id} style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontWeight: 800, fontSize: 14 }}>{exam.name}</span>
                {exam.summary.percent != null && (
                  <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-2)' }}>
                    {exam.summary.marks_obtained}/{exam.summary.marks_max} · <b style={{ color: 'var(--text)' }}>{exam.summary.percent}%</b>
                    {exam.summary.grade_label ? <span style={gradeChip}>{exam.summary.grade_label}</span> : null}
                  </span>
                )}
              </div>

              {exam.subjects.length > 0 && (
                <table style={tbl}>
                  <thead><tr><th style={{ ...th, textAlign: 'left' }}>Subject</th><th style={th}>Marks</th><th style={th}>%</th><th style={th}>Grade</th></tr></thead>
                  <tbody>
                    {exam.subjects.map((s, i) => (
                      <tr key={i}>
                        <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{s.name}</td>
                        <td style={cell}>{s.is_absent ? 'AB' : (s.marks_obtained != null ? `${s.marks_obtained} / ${s.max_marks ?? '—'}` : '—')}</td>
                        <td style={cell}>{s.percent != null ? `${s.percent}%` : '—'}</td>
                        <td style={cell}>{s.grade_label ? <span style={gradeChip}>{s.grade_label}</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {exam.skills.length > 0 && (
                <div style={{ padding: '12px 16px', borderTop: exam.subjects.length ? '1px solid var(--divider)' : 'none' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>Co-Scholastic</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {exam.skills.map((s, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 999, fontSize: 13 }}>
                        {s.name} {s.is_absent ? <b>—</b> : <b style={gradeChip}>{s.grade_label || '—'}</b>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Picker({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field" style={{ gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{label}</span>
      {children}
    </label>
  );
}

const tbl: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 };
const th: React.CSSProperties = { padding: '10px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, color: 'var(--text-2)', textAlign: 'center', borderBottom: '1px solid var(--divider)' };
const cell: React.CSSProperties = { padding: '11px 16px', textAlign: 'center', borderBottom: '1px solid var(--divider)', fontSize: 13.5 };
const gradeChip: React.CSSProperties = { display: 'inline-block', marginLeft: 6, padding: '1px 9px', borderRadius: 999, background: 'var(--slate-soft)', color: 'var(--text)', fontWeight: 700, fontSize: 12 };
